/**
 * TanStack Query hooks for `affiliate-referral` (fs809).
 *
 * Queries:
 *   - useReferralCode(affiliateId)      → the affiliate's referral code row
 *   - useReferrals(referrerId)          → referred publishers (list)
 *   - useReferrerSummary(referrerId)    → summary totals for tiles
 *
 * Mutations:
 *   - useMintCode()    — create-or-fetch a code for an affiliate
 *   - useRotateCode()  — rotate a code (invalidates list + code)
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	affiliateReferralQueryKeys,
	getCode,
	getReferrerSummary,
	listReferrals,
	mintCode,
	rotateCode,
	type ReferralCodeRow,
	type ReferralRow,
	type ReferrerSummary,
} from "./api";

export function useReferralCode(
	affiliateId: string,
): UseQueryResult<ReferralCodeRow | null, Error> {
	return useQuery({
		queryKey: affiliateReferralQueryKeys.code(affiliateId),
		queryFn: () => getCode(affiliateId),
		enabled: affiliateId.length > 0,
	});
}

export function useReferrals(
	referrerId: string,
): UseQueryResult<ReferralRow[], Error> {
	return useQuery({
		queryKey: affiliateReferralQueryKeys.referrals(referrerId),
		queryFn: () => listReferrals({ referrer_id: referrerId }),
		enabled: referrerId.length > 0,
	});
}

export function useReferrerSummary(
	referrerId: string,
): UseQueryResult<ReferrerSummary, Error> {
	return useQuery({
		queryKey: affiliateReferralQueryKeys.summary(referrerId),
		queryFn: () => getReferrerSummary(referrerId),
		enabled: referrerId.length > 0,
	});
}

export function useMintCode(): UseMutationResult<
	ReferralCodeRow,
	Error,
	string
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (affiliateId: string) => mintCode(affiliateId),
		onSuccess: (row) => {
			qc.setQueryData(
				affiliateReferralQueryKeys.code(row.affiliate_id),
				row,
			);
			qc.invalidateQueries({ queryKey: affiliateReferralQueryKeys.all });
		},
	});
}

export function useRotateCode(): UseMutationResult<
	ReferralCodeRow,
	Error,
	string
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (affiliateId: string) => rotateCode(affiliateId),
		onSuccess: (row) => {
			qc.setQueryData(
				affiliateReferralQueryKeys.code(row.affiliate_id),
				row,
			);
			qc.invalidateQueries({ queryKey: affiliateReferralQueryKeys.all });
		},
	});
}
